import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Grid, IconButton, Modal } from "@mui/material";
import { useRef, useState } from "react";
import { MAX_IMAGES } from "../../config/config";
import { StorageImage } from "../../data/providers/StorageProvider";

type MenuImageUploaderProps = {
  images: StorageImage[];
  onAddImages: (files: File[]) => void;
  onRemoveImage: (img: StorageImage) => void;
  loading?: boolean;
};

export function MenuImageUploader({
  images,
  onAddImages,
  onRemoveImage,
  loading = false,
}: MenuImageUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewImg, setPreviewImg] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) {
      onAddImages(files);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={2}>
        {images.map((img, idx) => (
          <Grid item key={img.fullPath || idx}>
            <Box
              sx={{
                position: "relative",
                width: 120,
                height: 120,
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid #ccc",
              }}
            >
              <img
                src={img.url}
                alt={`Carta ${idx + 1}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  cursor: "pointer",
                }}
                onClick={() => setPreviewImg(img.url)}
              />
              <IconButton
                size="small"
                sx={{
                  position: "absolute",
                  top: 2,
                  right: 2,
                  bgcolor: "rgba(255,255,255,0.7)",
                }}
                disabled={loading}
                onClick={() => onRemoveImage(img)}
              >
                <DeleteIcon fontSize="small" sx={{ color: "#ff5252" }} />
              </IconButton>
            </Box>
          </Grid>
        ))}
        {images.length < MAX_IMAGES && (
          <Grid item>
            <Box
              sx={{
                width: 120,
                height: 120,
                border: "1.5px dashed #bbb",
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: loading ? "not-allowed" : "pointer",
              }}
              onClick={() => !loading && fileInputRef.current?.click()}
            >
              <IconButton disabled={loading}>
                <AddIcon fontSize="large" color="primary" />
              </IconButton>
              <input
                type="file"
                accept="image/*"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleInputChange}
                disabled={loading}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      <Modal open={!!previewImg} onClose={() => setPreviewImg(null)}>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0,0,0,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <IconButton
            onClick={() => setPreviewImg(null)}
            sx={{
              position: "absolute",
              top: 20,
              right: 30,
              color: "#fff",
              bgcolor: "rgba(0,0,0,0.4)",
            }}
          >
            <Close />
          </IconButton>
          <img
            src={previewImg ?? ""}
            alt="Vista previa"
            style={{
              maxWidth: "90vw",
              maxHeight: "80vh",
              borderRadius: 12,
              boxShadow: "0 0 32px #0006",
              background: "#fff",
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
}
